/** @jsxImportSource @emotion/react */
import React, { memo, useState, useRef, useEffect } from 'react';
import { css } from '@emotion/react';

export interface PropertyPanelSection {
  id: string;
  label: string;
  type: 'select' | 'color' | 'number' | 'slider' | 'button-group' | 'text-input';
  value: any;
  icon?: string; // Основная иконка для отображения в панели
  options?: Array<{ value: any; label: string; icon?: string }>;
  min?: number;
  max?: number;
  step?: number;
  placeholder?: string;
  onChange: (value: any) => void;
}

interface PropertyPanelProps {
  x: number;
  y: number;
  sections: PropertyPanelSection[];
  onClose?: () => void;
}

const styles = {
  panel: css`
    position: fixed;
    background: white;
    border-radius: 24px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
    padding: 8px;
    z-index: 999999;
    display: flex;
    flex-direction: row;
    gap: 4px;
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  `,

  iconButton: css`
    position: relative;
    width: 40px;
    height: 40px;
    border: none;
    border-radius: 8px;
    background: transparent;
    cursor: pointer;
    font-size: 18px;
    transition: all 0.2s;
    color: #666;
    display: flex;
    align-items: center;
    justify-content: center;

    &:hover {
      background: #f5f5f5;
      color: #007ACC;
    }

    &.active {
      background: #E3F2FD;
      color: #007ACC;
    }
  `,

  dropdown: css`
    position: absolute;
    top: calc(100% + 8px);
    left: 50%;
    transform: translateX(-50%);
    background: white;
    border-radius: 12px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
    padding: 8px;
    z-index: 1000000;
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 150px;
  `,

  dropdownItem: css`
    padding: 8px 12px;
    border: none;
    border-radius: 6px;
    background: transparent;
    cursor: pointer;
    font-size: 14px;
    transition: all 0.2s;
    color: #333;
    display: flex;
    align-items: center;
    gap: 8px;
    text-align: left;
    white-space: nowrap;

    &:hover {
      background: #f5f5f5;
    }

    &.active {
      background: #E3F2FD;
      color: #007ACC;
      font-weight: 500;
    }
  `,

  colorPickerWrapper: css`
    position: relative;
  `,

  colorPreview: css`
    width: 24px;
    height: 24px;
    border-radius: 4px;
    border: 2px solid #ddd;
    cursor: pointer;
  `,

  colorInput: css`
    position: absolute;
    opacity: 0;
    width: 0;
    height: 0;
    pointer-events: none;
  `,

  separator: css`
    width: 1px;
    height: 24px;
    background: #e0e0e0;
    margin: 8px 4px;
  `,

  sliderWrapper: css`
    position: absolute;
    top: calc(100% + 8px);
    left: 50%;
    transform: translateX(-50%);
    background: white;
    border-radius: 12px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
    padding: 16px;
    z-index: 1000000;
    min-width: 200px;
  `,

  slider: css`
    width: 100%;
    height: 6px;
    border-radius: 3px;
    background: #e0e0e0;
    outline: none;
    -webkit-appearance: none;
    margin: 8px 0;

    &::-webkit-slider-thumb {
      -webkit-appearance: none;
      appearance: none;
      width: 16px;
      height: 16px;
      border-radius: 50%;
      background: #007ACC;
      cursor: pointer;
    }

    &::-moz-range-thumb {
      width: 16px;
      height: 16px;
      border-radius: 50%;
      background: #007ACC;
      cursor: pointer;
      border: none;
    }
  `,

  sliderValue: css`
    text-align: center;
    font-size: 14px;
    font-weight: 600;
    color: #007ACC;
  `,

  colorPalette: css`
    position: absolute;
    top: calc(100% + 8px);
    left: 50%;
    transform: translateX(-50%);
    background: white;
    border-radius: 12px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
    padding: 12px;
    z-index: 1000000;
    display: grid;
    grid-template-columns: repeat(5, 1fr);
    gap: 8px;
    min-width: 200px;
  `,

  textInputSection: css`
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding: 2px 0;
  `,

  textInputLabel: css`
    font-size: 11px;
    color: #888;
    font-weight: 600;
    padding-left: 2px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  `,

  textInput: css`
    padding: 6px 10px;
    border: 1.5px solid #e0e0e0;
    border-radius: 6px;
    font-size: 13px;
    outline: none;
    width: 260px;
    box-sizing: border-box;
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    color: #333;

    &:focus {
      border-color: #007ACC;
    }
  `,

  colorSwatch: css`
    width: 32px;
    height: 32px;
    border-radius: 6px;
    border: 2px solid transparent;
    cursor: pointer;
    transition: all 0.2s;
    position: relative;

    &:hover {
      transform: scale(1.1);
      border-color: #007ACC;
    }

    &.active {
      border-color: #007ACC;
      border-width: 3px;
      
      &::after {
        content: '✓';
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        color: white;
        font-size: 16px;
        font-weight: bold;
        text-shadow: 0 0 2px rgba(0, 0, 0, 0.5);
      }
    }
  `,
};

// Предустановленные цвета для палитры
const DEFAULT_COLORS = [
  '#000000', '#424242', '#666666', '#9E9E9E', '#BDBDBD',
  '#E0E0E0', '#F5F5F5', '#FFFFFF', '#D32F2F', '#E57373',
  '#F44336', '#EF5350', '#1976D2', '#42A5F5', '#2196F3',
  '#64B5F6', '#388E3C', '#66BB6A', '#4CAF50', '#81C784',
  '#FFA000', '#FFB74D', '#FF9800', '#FFCA28', '#7B1FA2',
  '#BA68C8', '#9C27B0', '#CE93D8', '#F57C00', '#FFB74D',
];

export const PropertyPanel: React.FC<PropertyPanelProps> = memo(({ x, y, sections, onClose }) => {
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const dropdownRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const isSelectingRef = useRef(false);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const onCloseRef = useRef(onClose);

  // Keep onCloseRef fresh on every render
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  const hasTextInput = sections.some((s) => s.type === 'text-input');

  // Close entire panel when clicking outside
  useEffect(() => {
    if (!onClose) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        onCloseRef.current?.();
      }
    };

    const timer = setTimeout(() => {
      document.addEventListener('click', handleClickOutside);
    }, 150);

    return () => {
      clearTimeout(timer);
      document.removeEventListener('click', handleClickOutside);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // run once on mount

  // Закрываем dropdown при клике вне
  useEffect(() => {
    if (!openDropdown) return;

    const handleClickOutside = (e: MouseEvent) => {
      // Проверяем, что клик не внутри текущего dropdown и не внутри панели
      const currentDropdown = dropdownRefs.current[openDropdown];
      const clickedInside =
        (currentDropdown && currentDropdown.contains(e.target as Node)) ||
        (panelRef.current && panelRef.current.contains(e.target as Node));

      if (!clickedInside) {
        setOpenDropdown(null);
      }
    };

    // Добавляем задержку чтобы не закрыть сразу после открытия
    const timer = setTimeout(() => {
      document.addEventListener('click', handleClickOutside);
    }, 100);

    return () => {
      clearTimeout(timer);
      document.removeEventListener('click', handleClickOutside);
    };
  }, [openDropdown]);

  const handleChange = (sectionId: string, value: any) => {
    const section = sections.find(s => s.id === sectionId);
    if (section) {
      console.log('PropertyPanel: handleChange', { sectionId, value, currentValue: section.value, section });
      isSelectingRef.current = true;

      // Вызываем onChange
      section.onChange(value);
      console.log('PropertyPanel: onChange called successfully for', sectionId, 'with value', value);

      // Закрываем dropdown
      setOpenDropdown(null);

      // НЕ закрываем панель автоматически - пользователь может захотеть изменить ещё что-то
      // Панель закроется при клике вне её
      isSelectingRef.current = false;
    } else {
      console.error('PropertyPanel: section not found for', sectionId);
    }
  };

  const toggleDropdown = (sectionId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setOpenDropdown(openDropdown === sectionId ? null : sectionId);
  };

  const renderTextInput = (section: PropertyPanelSection) => (
    <div css={styles.textInputSection}>
      <span css={styles.textInputLabel}>{section.label}</span>
      <input
        type="text"
        css={styles.textInput}
        value={section.value ?? ''}
        placeholder={section.placeholder || ''}
        onChange={(e) => section.onChange(e.target.value)}
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        onKeyDown={(e) => {
          if (e.key === 'Escape') onCloseRef.current?.();
          e.stopPropagation();
        }}
      />
    </div>
  );

  const renderIconButton = (section: PropertyPanelSection) => {
    const isOpen = openDropdown === section.id;

    return (
      <div key={section.id} style={{ position: 'relative' }}>
        <button
          css={styles.iconButton}
          className={isOpen ? 'active' : ''}
          onClick={(e) => {
            toggleDropdown(section.id, e);
          }}
          title={section.label}
        >
          {section.type === 'color' ? (
            <div style={{
              width: 24,
              height: 24,
              borderRadius: 4,
              background: section.value,
              border: '2px solid #ddd'
            }} />
          ) : (
            section.icon
          )}
        </button>

        {/* Выпадающий список для select и button-group */}
        {isOpen && (section.type === 'select' || section.type === 'button-group') && section.options && (
          <div
            ref={(el) => (dropdownRefs.current[section.id] = el)}
            css={styles.dropdown}
            role="presentation"
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          >
            {section.options.map((option) => (
              <button
                key={option.value}
                css={styles.dropdownItem}
                className={section.value === option.value ? 'active' : ''}
                onClick={(e) => {
                  console.log('Option clicked:', { sectionId: section.id, optionValue: option.value, optionLabel: option.label });
                  e.stopPropagation();
                  handleChange(section.id, option.value);
                }}
                onMouseDown={(e) => e.stopPropagation()}
              >
                {option.icon && <span style={{ fontSize: '16px' }}>{option.icon}</span>}
                <span>{option.label}</span>
              </button>
            ))}
          </div>
        )}

        {/* Слайдер */}
        {isOpen && section.type === 'slider' && (
          <div
            ref={(el) => (dropdownRefs.current[section.id] = el)}
            css={styles.sliderWrapper}
            role="presentation"
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          >
            <div css={styles.sliderValue}>{section.value}</div>
            <input
              type="range"
              css={styles.slider}
              value={section.value}
              min={section.min}
              max={section.max}
              step={section.step}
              onChange={(e) => {
                handleChange(section.id, Number(e.target.value));
              }}
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
            />
          </div>
        )}

        {/* Кастомная палитра цветов */}
        {isOpen && section.type === 'color' && (
          <div
            ref={(el) => (dropdownRefs.current[section.id] = el)}
            css={styles.colorPalette}
            role="presentation"
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          >
            {DEFAULT_COLORS.map((color) => (
              <button
                key={color}
                css={styles.colorSwatch}
                className={section.value.toUpperCase() === color.toUpperCase() ? 'active' : ''}
                style={{ background: color }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleChange(section.id, color);
                }}
                onMouseDown={(e) => e.stopPropagation()}
                title={color}
              />
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div
      ref={panelRef}
      css={styles.panel}
      style={{
        left: x,
        top: y,
        flexDirection: hasTextInput ? 'column' : 'row',
        borderRadius: hasTextInput ? 12 : 24,
        padding: hasTextInput ? '12px 14px' : '8px',
        minWidth: hasTextInput ? 'auto' : undefined,
        gap: hasTextInput ? 10 : 4,
      }}
      data-property-panel="true"
      role="presentation"
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
    >
      {sections.map((section, index) => (
        <React.Fragment key={section.id}>
          {section.type === 'text-input'
            ? renderTextInput(section)
            : renderIconButton(section)
          }
          {!hasTextInput && index < sections.length - 1 && section.type !== sections[index + 1]?.type && (
            <div css={styles.separator} />
          )}
        </React.Fragment>
      ))}
    </div>
  );
});

PropertyPanel.displayName = 'PropertyPanel';

